import React from "react"
import { useController, useFormContext } from "react-hook-form"

import { ErrorMessages, Group, Label, Stack } from "@/components"
import { SelectableBadge } from "@/components/SelectableBadge"

import classes from "@/components/Controlled/ControlledSelectableBadge/ControlledSelectableBadge.module.css"

export interface ControlledSelectableBadgeProps {
    label: string
    data
    rules?
    name: string
    transform?
    withAsterisk?: boolean
}

export const ControlledSelectableBadge = ({
    data,
    label,
    rules,
    name,
    transform,
    withAsterisk,
}: ControlledSelectableBadgeProps) => {
    const { control, trigger } = useFormContext()
    const { field, fieldState } = useController({
        name,
        control,
        rules,
    })

    const transformInput = React.useCallback(
        (value) => (transform?.input ? transform.input(value) : value),
        [transform],
    )
    const transformOutput = React.useCallback(
        (value) => (transform?.output ? transform.output(value) : value),
        [transform],
    )

    const isPillSelected = (pillValue: number) => transformInput(field.value).includes(pillValue)

    const onPillClick = ({ pillValue, isSelected }: { pillValue: number; isSelected: boolean }) => {
        field.onChange(
            transformOutput(
                isSelected
                    ? transformInput(field.value).filter((i) => i !== pillValue)
                    : [...transformInput(field.value), pillValue],
            ),
        )
        trigger(name)
    }

    return (
        <Stack spacing="6px">
            <Label label={label} className={classes.label} withAsterisk={rules?.required || withAsterisk} />
            <Group spacing="6px">
                {data.map((pill) => (
                    <SelectableBadge
                        key={pill.value}
                        active={isPillSelected(pill.value)}
                        text={pill.label}
                        onClick={() =>
                            onPillClick({
                                pillValue: pill.value,
                                isSelected: isPillSelected(pill.value),
                            })
                        }
                    />
                ))}
            </Group>
            {fieldState.error ? <ErrorMessages fieldError={fieldState.error} /> : null}
        </Stack>
    )
}
